/**
 * Login da porta (ORD-F-153; docs/DECISOES.md §84): o Firebase Authentication pela API REST, sem o SDK (nenhuma
 * dependência nova, nenhum script de terceiro, nenhum cookie, nenhum reCAPTCHA). Três chamadas, todas `POST`:
 *   - entrar: `identitytoolkit.googleapis.com/v1/accounts:signInWithPassword` (e-mail e senha);
 *   - esqueci a senha: `identitytoolkit.googleapis.com/v1/accounts:sendOobCode` (`PASSWORD_RESET`, o e-mail do Firebase);
 *   - renovar: `securetoken.googleapis.com/v1/token` (`grant_type=refresh_token`).
 * O `fetch` mora aqui (AC-2.2-01). Nada daqui lança: cada chamada devolve um resultado com o motivo, e a camada de cima
 * decide o texto (neutro: nunca diz se o e-mail existe). `credentials: 'omit'` (sem cookie) e `referrerPolicy:
 * 'strict-origin'`: a chave pública é restrita aos referenciadores do mapa.voto no console, e a origem basta para isso.
 */

export const AUTH_HOSTS = ['https://identitytoolkit.googleapis.com', 'https://securetoken.googleapis.com'] as const;

const SIGN_IN_URL = `${AUTH_HOSTS[0]}/v1/accounts:signInWithPassword`;
const OOB_URL = `${AUTH_HOSTS[0]}/v1/accounts:sendOobCode`;
const REFRESH_URL = `${AUTH_HOSTS[1]}/v1/token`;

/** Teto de cada chamada: sem resposta nesse tempo, vale como falha de rede (a sessão aberta não cai por isso). */
export const AUTH_TIMEOUT_MS = 15_000;

/** Tokens de uma sessão (o que a API devolve, já com a validade em ms de época). */
export interface AuthTokens {
  email: string;
  idToken: string;
  refreshToken: string;
  /** Fim da validade do `idToken` (ms de época, relógio do aparelho). */
  expiresAt: number;
}

/**
 * Motivos de recusa: `credentials` (e-mail ou senha errados, conta desativada, e-mail inválido: o texto é o mesmo),
 * `throttled` (muitas tentativas), `config` (sem chave, chave recusada ou provedor desligado), `network` (rede, tempo,
 * 5xx, resposta ilegível) e `revoked` (só na renovação: o refresh token não vale mais).
 */
export type AuthFailure = 'credentials' | 'throttled' | 'config' | 'network' | 'revoked';

export type SignInResult = { ok: true; tokens: AuthTokens } | { ok: false; reason: AuthFailure };
export type ResetResult = { ok: true } | { ok: false; reason: 'throttled' | 'config' | 'network' };
export type RefreshResult = { ok: true; tokens: Omit<AuthTokens, 'email'> } | { ok: false; reason: AuthFailure };

export interface AuthClientOptions {
  /** A chave pública do app web (VITE_AUTH_API_KEY); vazia = `config` sem pedido nenhum. */
  apiKey: string;
  fetchImpl?: typeof fetch;
  /** Relógio do aparelho (validade do token). */
  now?: () => number;
  timeoutMs?: number;
}

/** Erros da API do Firebase Auth que dizem "este refresh token não vale mais" (a sessão é apagada). */
const REVOKED = new Set([
  'TOKEN_EXPIRED',
  'USER_DISABLED',
  'USER_NOT_FOUND',
  'INVALID_REFRESH_TOKEN',
  'MISSING_REFRESH_TOKEN',
]);
/** Erros de e-mail e senha (o texto é o mesmo para todos: não diz qual dos dois errou nem se a conta existe). */
const CREDENTIALS = new Set([
  'INVALID_LOGIN_CREDENTIALS',
  'EMAIL_NOT_FOUND',
  'INVALID_PASSWORD',
  'USER_DISABLED',
  'INVALID_EMAIL',
  'MISSING_PASSWORD',
  'MISSING_EMAIL',
]);
/** Configuração do projeto (chave, provedor, domínio): o login não funciona até o console ser ajustado. */
const CONFIG = new Set([
  'API_KEY_INVALID',
  'API_KEY_EXPIRED',
  'OPERATION_NOT_ALLOWED',
  'PASSWORD_LOGIN_DISABLED',
  'PROJECT_NOT_FOUND',
  'CONFIGURATION_NOT_FOUND',
  'INVALID_API_KEY',
]);

/** O código de erro da API ("TOO_MANY_ATTEMPTS_TRY_LATER : ..." → "TOO_MANY_ATTEMPTS_TRY_LATER"). */
export function authErrorCode(body: unknown): string {
  const msg = (body as { error?: { message?: unknown } } | null)?.error?.message;
  if (typeof msg !== 'string') return '';
  return msg.split(':')[0]?.trim() ?? '';
}

/** Classifica uma resposta de erro (status + código) num motivo. */
export function classifyAuthError(status: number, code: string, refresh: boolean): AuthFailure {
  if (status >= 500 || status === 0) return 'network';
  if (code.startsWith('TOO_MANY_ATTEMPTS')) return 'throttled';
  if (refresh) return REVOKED.has(code) ? 'revoked' : CONFIG.has(code) || status === 403 ? 'config' : 'network';
  if (CREDENTIALS.has(code)) return 'credentials';
  if (CONFIG.has(code) || status === 403) return 'config';
  return status === 400 ? 'credentials' : 'network';
}

interface Raw {
  status: number;
  body: unknown;
}

async function post(o: AuthClientOptions, url: string, body: string, contentType: string): Promise<Raw | null> {
  const fetchImpl = o.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), o.timeoutMs ?? AUTH_TIMEOUT_MS) : null;
  try {
    const res = await fetchImpl(`${url}?key=${encodeURIComponent(o.apiKey)}`, {
      method: 'POST',
      headers: { 'Content-Type': contentType },
      body,
      cache: 'no-store',
      credentials: 'omit',
      referrerPolicy: 'strict-origin',
      ...(ctrl ? { signal: ctrl.signal } : {}),
    });
    let parsed: unknown = null;
    try {
      parsed = await res.json();
    } catch {
      parsed = null;
    }
    return { status: res.status, body: parsed };
  } catch {
    return null;
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
/** "3600" → 3600 s; ilegível, 1 h (a validade do token do Firebase). */
const seconds = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 3600;
};

/** Entrar com e-mail e senha. */
export async function signInWithPassword(o: AuthClientOptions, email: string, password: string): Promise<SignInResult> {
  if (!o.apiKey) return { ok: false, reason: 'config' };
  const raw = await post(
    o,
    SIGN_IN_URL,
    JSON.stringify({ email, password, returnSecureToken: true }),
    'application/json',
  );
  if (!raw) return { ok: false, reason: 'network' };
  if (raw.status !== 200) {
    return { ok: false, reason: classifyAuthError(raw.status, authErrorCode(raw.body), false) };
  }
  const b = (raw.body ?? {}) as Record<string, unknown>;
  const idToken = str(b.idToken);
  const refreshToken = str(b.refreshToken);
  if (!idToken || !refreshToken) return { ok: false, reason: 'network' };
  const now = (o.now ?? Date.now)();
  return {
    ok: true,
    tokens: { email: str(b.email) || email, idToken, refreshToken, expiresAt: now + seconds(b.expiresIn) * 1000 },
  };
}

/** "Esqueci a senha": o Firebase manda o e-mail de redefinição. E-mail sem conta também dá `ok` (texto neutro). */
export async function sendPasswordReset(o: AuthClientOptions, email: string): Promise<ResetResult> {
  if (!o.apiKey) return { ok: false, reason: 'config' };
  const raw = await post(o, OOB_URL, JSON.stringify({ requestType: 'PASSWORD_RESET', email }), 'application/json');
  if (!raw) return { ok: false, reason: 'network' };
  if (raw.status === 200) return { ok: true };
  const code = authErrorCode(raw.body);
  // sem dizer se o e-mail existe: "não encontrado" e "inválido" valem como enviado
  if (code === 'EMAIL_NOT_FOUND' || code === 'INVALID_EMAIL' || code === 'MISSING_EMAIL') return { ok: true };
  const reason = classifyAuthError(raw.status, code, false);
  return { ok: false, reason: reason === 'throttled' || reason === 'config' ? reason : 'network' };
}

/** Renova o `idToken` pelo refresh token. `revoked` só quando a API diz que ele não vale mais. */
export async function refreshIdToken(o: AuthClientOptions, refreshToken: string): Promise<RefreshResult> {
  if (!o.apiKey) return { ok: false, reason: 'config' };
  const raw = await post(
    o,
    REFRESH_URL,
    `grant_type=refresh_token&refresh_token=${encodeURIComponent(refreshToken)}`,
    'application/x-www-form-urlencoded',
  );
  if (!raw) return { ok: false, reason: 'network' };
  if (raw.status !== 200) {
    return { ok: false, reason: classifyAuthError(raw.status, authErrorCode(raw.body), true) };
  }
  const b = (raw.body ?? {}) as Record<string, unknown>;
  const idToken = str(b.id_token);
  const next = str(b.refresh_token) || refreshToken;
  if (!idToken) return { ok: false, reason: 'network' };
  const now = (o.now ?? Date.now)();
  return { ok: true, tokens: { idToken, refreshToken: next, expiresAt: now + seconds(b.expires_in) * 1000 } };
}
